import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, FileText, CheckCircle2, XCircle, Clock, Download, Users, GraduationCap, CalendarDays, Pencil } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MODULES_FORMATION = [
  "Préparation Bubble Tea",
  "Hygiène & HACCP",
  "Service client",
  "Caisse & encaissement",
  "Procédures ouverture/fermeture",
  "Traçabilité Traqfood",
  "Gestion stock & FIFO",
  "Préparation food (poke/bowls)",
  "Étiquetage",
  "Nettoyage & désinfection",
  "Gestion des allergènes",
  "Autre",
];

const statusLabel = { brouillon: 'Brouillon', en_cours: 'En cours', valide: 'Validé', termine: 'Terminé' };
const statusColor = { brouillon: 'bg-gray-100 text-gray-700', en_cours: 'bg-amber-100 text-amber-700', valide: 'bg-green-100 text-green-700', termine: 'bg-blue-100 text-blue-700' };

export default function FormationPage() {
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [plans, setPlans] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | detail | edit
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [synthesis, setSynthesis] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);

  // Form state
  const [form, setForm] = useState({ titre: '', employe_id: '', employe_nom: '', date_debut: '', date_fin: '', notes: '', sessions: [] });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, eRes] = await Promise.allSettled([api.get('/formation/plans'), api.get('/rh/employes')]);
      if (pRes.status === 'fulfilled') setPlans(pRes.value.data);
      if (eRes.status === 'fulfilled') setEmployes(eRes.value.data);
    } catch {} finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openDetail = async (plan) => {
    setSelectedPlan(plan);
    try {
      const res = await api.get(`/formation/plans/${plan.id}/synthesis`);
      setSynthesis(res.data);
    } catch { setSynthesis(null); }
    setView('detail');
  };

  const openEdit = (plan) => {
    setForm({
      titre: plan.titre || '',
      employe_id: plan.employe_id || '',
      employe_nom: plan.employe_nom || '',
      date_debut: plan.date_debut || '',
      date_fin: plan.date_fin || '',
      notes: plan.notes || '',
      sessions: plan.sessions || [],
    });
    setSelectedPlan(plan);
    setView('edit');
  };

  const openCreate = () => {
    setForm({ titre: '', employe_id: '', employe_nom: '', date_debut: '', date_fin: '', notes: '', sessions: [] });
    setSelectedPlan(null);
    setCreateDialog(true);
  };

  const createPlan = async () => {
    if (!form.employe_id || !form.titre) { toast.error('Employé et titre requis'); return; }
    try {
      const emp = employes.find(e => e.id === form.employe_id);
      const res = await api.post('/formation/plans', { ...form, employe_nom: emp?.prenom || form.employe_nom });
      toast.success('Plan créé');
      setCreateDialog(false);
      fetchAll();
      openEdit(res.data);
    } catch { toast.error('Erreur'); }
  };

  const savePlan = async () => {
    if (!selectedPlan) return;
    try {
      await api.put(`/formation/plans/${selectedPlan.id}`, form);
      toast.success('Plan enregistré');
      const res = await api.get(`/formation/plans/${selectedPlan.id}`);
      setSelectedPlan(res.data);
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm('Supprimer ce plan ?')) return;
    try {
      await api.delete(`/formation/plans/${planId}`);
      toast.success('Plan supprimé');
      setView('list');
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const validateEmployer = async (planId) => {
    try {
      await api.post(`/formation/plans/${planId}/validate-employer`);
      toast.success('Validation employeur enregistrée');
      const res = await api.get(`/formation/plans/${planId}`);
      setSelectedPlan(res.data);
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const validateEmployee = async (planId) => {
    try {
      await api.post(`/formation/plans/${planId}/validate-employee`);
      toast.success('Validation employé enregistrée');
      const res = await api.get(`/formation/plans/${planId}`);
      setSelectedPlan(res.data);
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const toggleSession = async (planId, sessionId) => {
    try {
      await api.post(`/formation/plans/${planId}/toggle-session/${sessionId}`);
      const res = await api.get(`/formation/plans/${planId}`);
      setSelectedPlan(res.data);
      const sRes = await api.get(`/formation/plans/${planId}/synthesis`);
      setSynthesis(sRes.data);
    } catch { toast.error('Erreur'); }
  };

  const downloadPdf = (planId) => {
    const token = localStorage.getItem('token');
    window.open(`${API}/api/formation/plans/${planId}/pdf?token=${token}`, '_blank');
  };

  const addSession = () => {
    setForm(f => ({
      ...f,
      sessions: [...f.sessions, { id: crypto.randomUUID(), date: '', heure_debut: '09:00', heure_fin: '12:00', module: '', description: '', formateur: '', completed: false }],
    }));
  };

  const updateSession = (idx, field, val) => {
    setForm(f => {
      const sessions = [...f.sessions];
      sessions[idx] = { ...sessions[idx], [field]: val };
      return { ...f, sessions };
    });
  };

  const removeSession = (idx) => {
    setForm(f => ({ ...f, sessions: f.sessions.filter((_, i) => i !== idx) }));
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  // EDIT VIEW
  if (view === 'edit') {
    return (
      <div className="space-y-6 max-w-4xl" data-testid="formation-edit-view">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">{selectedPlan ? 'Modifier le plan' : 'Nouveau plan'}</h1>
            <p className="text-muted-foreground text-sm">{form.employe_nom}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setView('list'); }} data-testid="formation-back-list">Retour</Button>
            <Button onClick={savePlan} data-testid="formation-save-btn">Enregistrer</Button>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Titre</Label><Input value={form.titre} onChange={e => setForm(f => ({...f, titre: e.target.value}))} data-testid="formation-titre" /></div>
              <div><Label>Employé</Label><Input value={form.employe_nom} disabled className="bg-muted" /></div>
              <div><Label>Date début</Label><Input type="date" value={form.date_debut} onChange={e => setForm(f => ({...f, date_debut: e.target.value}))} data-testid="formation-date-debut" /></div>
              <div><Label>Date fin</Label><Input type="date" value={form.date_fin} onChange={e => setForm(f => ({...f, date_fin: e.target.value}))} data-testid="formation-date-fin" /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={2} data-testid="formation-notes" /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-base">Sessions de formation ({form.sessions.length})</CardTitle>
              <Button size="sm" onClick={addSession} data-testid="formation-add-session"><Plus className="w-4 h-4 mr-1" /> Ajouter une session</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {form.sessions.length === 0 && <p className="text-center text-muted-foreground py-4">Aucune session. Cliquez sur "Ajouter une session".</p>}
            {form.sessions.map((s, i) => (
              <div key={s.id || i} className="border rounded-lg p-4 space-y-3" data-testid={`session-row-${i}`}>
                <div className="flex justify-between items-start">
                  <span className="text-sm font-bold text-muted-foreground">Session {i + 1}</span>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeSession(i)} data-testid={`session-delete-${i}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><Label className="text-xs">Date</Label><Input type="date" value={s.date} onChange={e => updateSession(i, 'date', e.target.value)} /></div>
                  <div><Label className="text-xs">Heure début</Label><Input type="time" value={s.heure_debut} onChange={e => updateSession(i, 'heure_debut', e.target.value)} /></div>
                  <div><Label className="text-xs">Heure fin</Label><Input type="time" value={s.heure_fin} onChange={e => updateSession(i, 'heure_fin', e.target.value)} /></div>
                  <div>
                    <Label className="text-xs">Module</Label>
                    <Select value={s.module} onValueChange={v => updateSession(i, 'module', v)}>
                      <SelectTrigger><SelectValue placeholder="Module" /></SelectTrigger>
                      <SelectContent>{MODULES_FORMATION.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Description</Label><Input value={s.description || ''} onChange={e => updateSession(i, 'description', e.target.value)} placeholder="Contenu de la session" /></div>
                  <div><Label className="text-xs">Formateur</Label><Input value={s.formateur || ''} onChange={e => updateSession(i, 'formateur', e.target.value)} placeholder="Nom du formateur" /></div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  // DETAIL VIEW
  if (view === 'detail' && selectedPlan) {
    const p = selectedPlan;
    const ve = p.validation_employeur || {};
    const va = p.validation_employe || {};
    const prog = synthesis?.progression || 0;

    return (
      <div className="space-y-6 max-w-4xl" data-testid="formation-detail-view">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">{p.titre}</h1>
            <p className="text-muted-foreground text-sm">{p.employe_nom} | {p.date_debut} → {p.date_fin}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setView('list')} data-testid="formation-back-list">Retour</Button>
            {isAdmin && <Button variant="outline" onClick={() => openEdit(p)} data-testid="formation-edit-btn"><Pencil className="w-4 h-4 mr-1" /> Modifier</Button>}
            <Button variant="outline" onClick={() => downloadPdf(p.id)} data-testid="formation-pdf-btn"><Download className="w-4 h-4 mr-1" /> PDF</Button>
          </div>
        </div>

        {/* Progression */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Progression : {synthesis?.sessions_completed || 0}/{synthesis?.total_sessions || 0} sessions ({synthesis?.total_heures || 0}h)</span>
              <Badge className={statusColor[p.statut]}>{statusLabel[p.statut]}</Badge>
            </div>
            <Progress value={prog} className="h-3" data-testid="formation-progress" />
          </CardContent>
        </Card>

        {/* Validations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Validation Employeur</h3>
                {ve.valide ? <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" /> Validé</Badge> : <Badge variant="outline" className="text-amber-600"><Clock className="w-3 h-3 mr-1" /> En attente</Badge>}
              </div>
              {ve.valide && <p className="text-sm text-muted-foreground">Par {ve.nom} le {ve.date?.slice(0, 10)}</p>}
              {isAdmin && !ve.valide && <Button className="w-full mt-2" onClick={() => validateEmployer(p.id)} data-testid="formation-validate-employer">Valider (Employeur)</Button>}
              {isAdmin && ve.valide && <Button variant="outline" className="w-full mt-2 text-red-500" onClick={async () => { await api.post(`/formation/plans/${p.id}/unvalidate-employer`); const r = await api.get(`/formation/plans/${p.id}`); setSelectedPlan(r.data); fetchAll(); }}>Annuler la validation</Button>}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Validation Employé</h3>
                {va.valide ? <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" /> Validé</Badge> : <Badge variant="outline" className="text-amber-600"><Clock className="w-3 h-3 mr-1" /> En attente</Badge>}
              </div>
              {va.valide && <p className="text-sm text-muted-foreground">Par {va.nom} le {va.date?.slice(0, 10)}</p>}
              {!va.valide && <Button className="w-full mt-2" variant="outline" onClick={() => validateEmployee(p.id)} data-testid="formation-validate-employee">Valider (Employé)</Button>}
              {va.valide && <Button variant="outline" className="w-full mt-2 text-red-500" onClick={async () => { await api.post(`/formation/plans/${p.id}/unvalidate-employee`); const r = await api.get(`/formation/plans/${p.id}`); setSelectedPlan(r.data); fetchAll(); }}>Annuler la validation</Button>}
            </CardContent>
          </Card>
        </div>

        {/* Synthesis by module */}
        {synthesis?.modules && Object.keys(synthesis.modules).length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Répartition par module</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Module</TableHead>
                    <TableHead className="text-center">Sessions</TableHead>
                    <TableHead className="text-center">Terminées</TableHead>
                    <TableHead className="text-center">Heures</TableHead>
                    <TableHead className="text-center">Avancement</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(synthesis.modules).map(([mod, info]) => (
                    <TableRow key={mod}>
                      <TableCell className="font-medium">{mod}</TableCell>
                      <TableCell className="text-center">{info.sessions}</TableCell>
                      <TableCell className="text-center">{info.completed}</TableCell>
                      <TableCell className="text-center">{info.heures.toFixed(1)}h</TableCell>
                      <TableCell className="text-center"><Progress value={info.sessions > 0 ? (info.completed / info.sessions) * 100 : 0} className="h-2 w-20 inline-block" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Sessions list */}
        <Card>
          <CardHeader><CardTitle className="text-base">Sessions de formation</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Horaire</TableHead>
                  <TableHead>Module</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Formateur</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(p.sessions || []).map(s => (
                  <TableRow key={s.id} className={s.completed ? 'opacity-60' : ''}>
                    <TableCell>
                      <Checkbox checked={s.completed || false} onCheckedChange={() => toggleSession(p.id, s.id)} data-testid={`session-check-${s.id}`} />
                    </TableCell>
                    <TableCell className="font-medium">{s.date}</TableCell>
                    <TableCell>{s.heure_debut} - {s.heure_fin}</TableCell>
                    <TableCell><Badge variant="secondary">{s.module}</Badge></TableCell>
                    <TableCell className="text-sm">{s.description}</TableCell>
                    <TableCell className="text-sm">{s.formateur}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {p.notes && (
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm"><span className="font-medium">Notes :</span> {p.notes}</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // LIST VIEW
  return (
    <div className="space-y-6" data-testid="formation-list-view">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Planning de Formation</h1>
          <p className="text-muted-foreground text-sm">{plans.length} plan(s) de formation</p>
        </div>
        <Button onClick={openCreate} data-testid="formation-create-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau plan
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total plans</p><p className="text-2xl font-bold">{plans.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">En cours</p><p className="text-2xl font-bold text-amber-600">{plans.filter(p => p.statut === 'en_cours').length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Validés</p><p className="text-2xl font-bold text-green-600">{plans.filter(p => p.statut === 'valide').length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total sessions</p><p className="text-2xl font-bold">{plans.reduce((s, p) => s + (p.sessions?.length || 0), 0)}</p></CardContent></Card>
      </div>

      {/* Plans table */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><GraduationCap className="w-5 h-5" /> Plans de formation</CardTitle></CardHeader>
        <CardContent>
          {plans.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Aucun plan de formation. Créez-en un !</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employé</TableHead>
                  <TableHead>Titre</TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead className="text-center">Sessions</TableHead>
                  <TableHead className="text-center">Employeur</TableHead>
                  <TableHead className="text-center">Employé</TableHead>
                  <TableHead className="text-center">Statut</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map(p => (
                  <TableRow key={p.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openDetail(p)} data-testid={`plan-row-${p.id}`}>
                    <TableCell className="font-medium">{p.employe_nom}</TableCell>
                    <TableCell>{p.titre}</TableCell>
                    <TableCell className="text-sm">{p.date_debut} → {p.date_fin}</TableCell>
                    <TableCell className="text-center">{p.sessions?.length || 0}</TableCell>
                    <TableCell className="text-center">
                      {p.validation_employeur?.valide ? <CheckCircle2 className="w-5 h-5 text-green-600 mx-auto" /> : <XCircle className="w-5 h-5 text-muted-foreground mx-auto" />}
                    </TableCell>
                    <TableCell className="text-center">
                      {p.validation_employe?.valide ? <CheckCircle2 className="w-5 h-5 text-green-600 mx-auto" /> : <XCircle className="w-5 h-5 text-muted-foreground mx-auto" />}
                    </TableCell>
                    <TableCell className="text-center"><Badge className={statusColor[p.statut]}>{statusLabel[p.statut]}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`plan-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => downloadPdf(p.id)} data-testid={`plan-pdf-${p.id}`}><Download className="w-4 h-4" /></Button>
                        {isAdmin && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deletePlan(p.id)} data-testid={`plan-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent data-testid="formation-create-dialog">
          <DialogHeader><DialogTitle>Nouveau plan de formation</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Employé</Label>
              <Select value={form.employe_id} onValueChange={v => { const emp = employes.find(e => e.id === v); setForm(f => ({...f, employe_id: v, employe_nom: emp?.prenom || ''})); }}>
                <SelectTrigger data-testid="formation-select-emp"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{employes.map(e => <SelectItem key={e.id} value={e.id}>{e.prenom} - {e.poste}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Titre du plan</Label><Input value={form.titre} onChange={e => setForm(f => ({...f, titre: e.target.value}))} placeholder="Ex: Formation intégration" data-testid="formation-input-titre" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date début</Label><Input type="date" value={form.date_debut} onChange={e => setForm(f => ({...f, date_debut: e.target.value}))} data-testid="formation-input-debut" /></div>
              <div><Label>Date fin</Label><Input type="date" value={form.date_fin} onChange={e => setForm(f => ({...f, date_fin: e.target.value}))} data-testid="formation-input-fin" /></div>
            </div>
            <Button className="w-full" onClick={createPlan} data-testid="formation-submit-btn">Créer le plan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
